import React, { useMemo } from "react";
import { Card, CardBody, CardHeader, Button } from "reactstrap";
import { RevenueCharts } from "./DashboardEcommerceCharts";
import { getRevenueQuery } from "../../queries";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import "dayjs/locale/uz";
import "dayjs/locale/uz-latn";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const Revenue = ({ dateRange }) => {
  const { t, i18n } = useTranslation();
  const { data: statsData } = useQuery({
    ...getRevenueQuery({
      fromCreatedAt: dateRange[0],
      toCreatedAt: dateRange[1],
    }),
  });

  const dates = useMemo(() => {
    const dates = statsData?.data?.data ?? [];

    const years = dates.map((stat) => dayjs(stat.date).year());
    const allSameYear = years.every((year) => year === years[0]);
    const dayjsLang = i18n.language === "uz" ? "uz-latn" : i18n.language === "uzc" ? "uz" : i18n.language === "en" ? "en" : "ru";

    return dates.map((stat) => {
      const d = dayjs(stat.date).locale(dayjsLang);
      return allSameYear
        ? d.format("DD MMM")
        : d.format("MMM");
    });
  }, [statsData, i18n.language]);

  const transformedStatsData = useMemo(
    () => [
      {
        name: t("Total Amount Payment"),
        type: "area",
        data: statsData?.data?.data.map(
          (item) => item.totalAmountPayment / 100
        ),
      },
      {
        name: t("Total Amount Transactions"),
        type: "bar",
        data: statsData?.data?.data.map(
          (item) => item.totalAmountTransactions / 100
        ),
      },
      {
        name: t("Total Amount Commission"),
        type: "line",
        data: statsData?.data?.data.map(
          (item) => item.totalAmountCommission / 100
        ),
      },
    ],
    [t, statsData]
  );

  // Export to Excel handler
  const handleExportExcel = () => {
    if (!statsData?.data?.data?.length) {
      alert(t("No data available to export"));
      return;
    }

    // Prepare worksheet data
    const worksheetData = statsData.data.data.map((item) => ({
      Date: dayjs(item.date).format("YYYY-MM-DD"),
      [t("Total Amount Payment")]: item.totalAmountPayment / 100,
      [t("Total Amount Transactions")]: item.totalAmountTransactions / 100,
      [t("Total Amount Commission")]: item.totalAmountCommission / 100,
    }));

    // Create worksheet and workbook
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Revenue");

    // Write workbook and create blob
    const wbout = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    // Save file
    saveAs(blob, `Revenue_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`);
  };

  return (
    <React.Fragment>
      <Card>
        <CardHeader className="border-0 align-items-center d-flex">
          <h4 className="card-title mb-0 flex-grow-1 fw-bold">
            {t("Revenue")}
          </h4>
          <Button color="primary" size="sm" onClick={handleExportExcel}>
            <i class="ri-file-excel-2-line"></i>
          </Button>
        </CardHeader>

        <CardHeader className="p-0 border-0 bg-light-subtle"></CardHeader>

        <CardBody className="p-0 pb-2">
          <div className="w-100">
            <div dir="ltr">
              <RevenueCharts
                dates={dates}
                series={transformedStatsData}
                dataColors='["--vz-primary", "--vz-success", "--vz-danger"]'
              />
            </div>
          </div>
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default Revenue;
